import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect } from 'react'
import Image from 'next/image'
import { Typography, Button, Stepper, StepLabel, Step } from '@material-ui/core'
import { useWeb3Context } from '../../contexts/Web3Context'
import Whitelist from '../../data/nft-whitelist.json'
import { ethers } from 'ethers'
import { ToastContainer } from 'react-toastify'
import NotWhitelisted from './NotWhitelisted'
import Whitelisted from './Whitelisted'
import MintOver from './MintOver'
import ReactMarkdown from 'react-markdown'

import 'react-toastify/dist/ReactToastify.css'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '1rem',
  },
  stepsSection: {
    width: '100%',
  },
  stepsContainer: {
    background: 'rgba(70, 43, 98, 1)',
    borderRadius: '.4rem',
    padding: '24px 8px',
  },
  steps: {
    width: '100%',
  },
  stepLabel: {
    fontSize: '14px',
    marginTop: '8px',
    color: 'rgba(255,255,255,0.6) !important',
  },
  activeStepLabel: {
    color: '#fff !important',
  },
  stepDescription: {
    color: 'white',
    fontSize: '14px',
    textAlign: 'center',
    padding: '1rem',
    background: 'rgba(43, 24, 65, 1)',
    borderBottomRightRadius: '.4rem',
    borderBottomLeftRadius: '.4rem',
    minHeight: '4.8rem',
  },
  notificationSection: {
    color: 'white',
    padding: '1rem',
    borderRadius: '.8rem',
    background: 'rgba(0, 0, 0, 0.4)',
    width: '100%',
    textAlign: 'center',

    '& a': {
      color: 'white',
      padding: '0.4rem',
    },

    '& p': {
      fontSize: '1rem',
    },

    '&.success': {
      background: 'rgba(95, 221, 183, 0.4)',
    },
    '&.error': {
      background: 'rgba(221, 95, 133, 0.4)',
    },
  },
  mintSection: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '10px',
    padding: '3rem',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
  },
  mint: {},
  mintButton: {
    padding: '1rem 4rem',
  },
  mintButtonDisabled: {
    backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
  },
  mintButtonLabel: {
    color: 'white',
  },
  pfpImage: {
    borderRadius: '.4rem',
  },
  loading: {
    color: 'white',
  },
}))

function getSteps() {
  return ['Whitelist', 'Public', 'Secondary']
}

function getStepContent(stepIndex: number) {
  switch (stepIndex) {
    case 0:
      return 'The mint is free for people who joined Cryptotesters community before March 2022.'
    case 1:
      return 'The exisiting community initially minted, and now the public is able to mint the NFTs which would grant access to the community.'
    case 2:
      return `All Membership NFTs are minted and can only be bought on [Quixotic](https://quixotic.io/collection/cryptotesters).`
    default:
      return 'Join the tester community by minting your membership NFT.'
  }
}

function MintCard(): JSX.Element {
  const classes = useStyles({})
  const [activeStep, setActiveStep] = React.useState(-1)
  const steps = getSteps()

  const {
    address,
    network,
    isPublicMint,
    connect,
    switchToOptimism,
    isWhitelistMint,
    currentTokenId,
    loading,
  } = useWeb3Context()

  const networkIsOptimism = () => {
    return [10].includes(network?.chainId || 0)
  }

  const mintIsOver = () => {
    if (currentTokenId) {
      return currentTokenId >= 2000
    }
    return false
  }

  const readyToMint = () => {
    return networkIsOptimism() && address && !mintIsOver() && !loading
  }

  const addressIsWhitelisted = () => {
    if (address)
      return (
        Whitelist.includes(address) ||
        Whitelist.includes(ethers.utils.getAddress(address)) ||
        Whitelist.includes(ethers.utils.getAddress(address).toLowerCase()) ||
        Whitelist.includes(ethers.utils.getAddress(address).toUpperCase())
      )
    return false
  }

  useEffect(() => {
    if (isWhitelistMint) {
      setActiveStep(0)
    }

    if (isPublicMint) {
      setActiveStep(1)
    }

    if (mintIsOver()) {
      return setActiveStep(3)
    }
  }, [isPublicMint, isWhitelistMint, addressIsWhitelisted, mintIsOver])

  return (
    <div className={`${classes.root}`}>
      <div className={classes.stepsSection}>
        <div className={classes.steps}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            classes={{ root: classes.stepsContainer }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  classes={{
                    label: classes.stepLabel,
                    active: classes.activeStepLabel,
                    completed: classes.stepLabel,
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>
        <div className={classes.stepDescription}>
          {<ReactMarkdown>{getStepContent(activeStep)}</ReactMarkdown>}
        </div>
      </div>

      <div className={classes.mintSection}>
        <div className={classes.mint}>
          <Image
            src="/mint/pfp/nft-small.gif"
            height={'160px'}
            width={'160px'}
            className={classes.pfpImage}
            loading="lazy"
          />
        </div>

        {/* when wallet is not connected */}
        {!network ? (
          <Button
            variant="contained"
            color="primary"
            classes={{
              root: classes.mintButton,
              disabled: classes.mintButtonDisabled,
              contained: classes.mintButton,
              label: classes.mintButtonLabel,
            }}
            onClick={() => {
              try {
                if (connect) connect()
              } catch (e) {
                console.log(e)
              }
            }}
          >
            <Typography variant="body2">Connect wallet</Typography>
          </Button>
        ) : null}
        {/* when wallet is  connected, but not on Optmism network */}
        {network && !networkIsOptimism() ? (
          <>
            <div className={`${classes.notificationSection} error`}>
              <Typography variant="body2">
                Mint is happening on the Optimism network fren.
              </Typography>
            </div>
            <Button
              variant="contained"
              color="primary"
              classes={{
                root: classes.mintButton,
                disabled: classes.mintButtonDisabled,
                contained: classes.mintButton,
                label: classes.mintButtonLabel,
              }}
              onClick={async () => {
                try {
                  if (switchToOptimism) await switchToOptimism()
                } catch (e) {
                  console.log(e)
                }
              }}
            >
              <Typography variant="subtitle2">👌🏾 Switch to Optimism</Typography>
            </Button>
          </>
        ) : null}
        {loading ? (
          <div>
            <Typography variant="subtitle2" className={classes.loading}>
              loading your destiny...
            </Typography>
          </div>
        ) : null}
        {readyToMint() && addressIsWhitelisted() ? <Whitelisted /> : null}
        {readyToMint() && !addressIsWhitelisted() ? <NotWhitelisted /> : null}
        {mintIsOver() ? <MintOver /> : null}
      </div>
      <ToastContainer />
    </div>
  )
}

export default MintCard
